/**
 * Settings Store - Impostazioni di sistema (formati, locale, etc.)
 * Per Gleem Core
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/core/services/api'

export interface SystemSettings {
  // Formato numeri: 'it' = 1.000,00 | 'en' = 1,000.00
  numberFormat: 'it' | 'en'
  // Valuta
  currency: string
  // Lingua
  language: string
  // Temperatura: 'C' | 'F'
  temperatureUnit: 'C' | 'F'
  // Sistema di misura: 'metric' | 'imperial'
  measurementSystem: 'metric' | 'imperial'
}

const DEFAULT_SETTINGS: SystemSettings = {
  numberFormat: 'it',
  currency: 'EUR',
  language: 'it',
  temperatureUnit: 'C',
  measurementSystem: 'metric'
}

export const useSettingsStore = defineStore('settings', () => {
  // State
  const settings = ref<SystemSettings>({ ...DEFAULT_SETTINGS })
  const isLoaded = ref(false)
  const isLoading = ref(false)

  // Computed
  const locale = computed(() => {
    return settings.value.numberFormat === 'it' ? 'it-IT' : 'en-US'
  })

  // Actions
  async function loadSettings() {
    if (isLoaded.value || isLoading.value) return

    // Non chiamare l'API se non c'è token — evita 401 loop su /login
    const token = localStorage.getItem('token')
    if (!token) {
      settings.value = { ...DEFAULT_SETTINGS }
      return
    }

    isLoading.value = true
    try {
      const response = await apiClient.get('/api/eb/settings')
      const data = response.data || {}

      // Map API response to settings
      settings.value = {
        numberFormat: data['system.number_format'] || DEFAULT_SETTINGS.numberFormat,
        currency: data['system.currency'] || DEFAULT_SETTINGS.currency,
        language: data['system.language'] || DEFAULT_SETTINGS.language,
        temperatureUnit: data['system.temperature_unit'] || DEFAULT_SETTINGS.temperatureUnit,
        measurementSystem: data['system.measurement_system'] || DEFAULT_SETTINGS.measurementSystem
      }

      isLoaded.value = true
    } catch (err) {
      console.error('Failed to load settings:', err)
      // Use defaults on error
      settings.value = { ...DEFAULT_SETTINGS }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Format a number according to user settings
   */
  function formatNumber(value: number | null | undefined, decimals: number = 2): string {
    if (value === null || value === undefined) return '-'
    return new Intl.NumberFormat(locale.value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value)
  }

  /**
   * Format currency according to user settings
   */
  function formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-'
    return new Intl.NumberFormat(locale.value, {
      style: 'currency',
      currency: settings.value.currency
    }).format(value)
  }

  /**
   * Format price (number with currency symbol after, e.g., "1.234,56 €")
   */
  function formatPrice(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-'
    const formatted = new Intl.NumberFormat(locale.value, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
    return formatted
  }

  return {
    // State
    settings,
    isLoaded,
    isLoading,
    // Computed
    locale,
    // Actions
    loadSettings,
    formatNumber,
    formatCurrency,
    formatPrice
  }
})
